import type { Language, Marketplace, PLATFORMS, SharedVarsMap } from '@amzn/journeys-common';
import type {
  AssetSearchResult,
  CampaignConfigProviderCampaignFragment,
  EligibilityCriteriaMarketplaceFragment,
  Maybe,
  TpCampaignRequiredFormFlavorFragment,
  TrafficCampaignCommonData,
  TrafficCampaignPlacementDefaultsFragment,
  TrafficEvent,
  TrafficPlacementGroupFragment,
  TrafficProject,
} from 'generated/graphql';
import type { FC } from 'react';

export type SummaryContentTableRowConfig = {
  type: 'text' | 'image';
  value: string;
  label: string;
};

/**
 * Function that returns the content table rows for a given flavor's content.
 */
export type GetSummaryContentTableRows<TContent> = (params: {
  /** Flavor's content */
  content: TContent;
  /** Flavor's creative name */
  creativeName: string;
  /** Assets associated with this project */
  projectAssets?: AssetSearchResult[];
  /** Campaign's shared vars map */
  sharedVarsMap: SharedVarsMap;
}) => SummaryContentTableRowConfig[];

// OVERVIEW SECTION

export type GetOverviewTableData<OverviewTableData> = (params: {
  name: string;
  common?: Maybe<TrafficCampaignCommonData>;
}) => OverviewTableData;

// biome-ignore lint/style/noEnum: legacy code
export enum ROW_TYPE {
  COLOR = 'color',
  TEXT = 'text',
  IMAGE = 'image',
  LINK = 'link',
  TEXT_WITH_WARNING = 'text_with_warning',
}

export type OverviewTableRowData = {
  label: string;
  value?: string | null;
};

export type ContentTableRowData = {
  label: string;
  type: ROW_TYPE;
  value?: string | null;
  secondaryText?: string;
};

export type ContentTableData = {
  rowContent: ContentTableRowData[];
};

export type GetLinkContentParams<TContent> = {
  content: TContent;
  contentName: string;
  sharedVarsMap: SharedVarsMap;
};

export type GetContentTableDataParams<TContent, TGlobalContent = undefined> = {
  projectAssets?: AssetSearchResult[];
  content: TContent;
  globalContent?: TGlobalContent;
  contentName: string;
  sharedVarsMap: SharedVarsMap;
  config?: CampaignConfigProviderCampaignFragment;
  platform?: PLATFORMS;
};

export type GetContentTableData<TContent, TGlobalContent> = (
  params: GetContentTableDataParams<TContent, TGlobalContent>,
) => ContentTableData;

export type ContentTableContentProps<TGlobalContent = undefined> = {
  rowName: string;
  project: TrafficProject;
  globalContent?: TGlobalContent;
  flavor: TpCampaignRequiredFormFlavorFragment;
  sharedVarsMap: SharedVarsMap;
  contentName: string;
  config?: CampaignConfigProviderCampaignFragment;
};

export type GetPlacementDisplayName = (params: {
  language: Language;
  platform?: PLATFORMS;
  marketplace: Marketplace;
  variant?: string;
  platformNameOverrides?: Partial<Record<PLATFORMS, string>>;
}) => string;

export type GetPlacementTableRowValuesParams = {
  flavor: TpCampaignRequiredFormFlavorFragment;
  platformValue: PLATFORMS;
  placementDefaults?: TrafficCampaignPlacementDefaultsFragment;
  placementGroup: TrafficPlacementGroupFragment & {
    placementGroupId: string;
  };
  projectEvents?: TrafficEvent[];
};

export type DateRangePlacementSummaryValues = {
  eventName: string;
  formattedEndTime: string;
  formattedStartTime: string;
};
export type TargetingPlacementSummaryValues = {
  marketplaceCriteria?: EligibilityCriteriaMarketplaceFragment[] | null;
  bullseyeUrl?: string | null;
};
export type ImpressionsPlacementSummaryValues = {
  impressionDaysTimeout?: string | null;
  impressionLimit?: string | null;
};
export type WeblabPlacementSummaryValues = {
  weblabName?: string | null;
  weblabTreatment?: string | null;
};

export type PlacementTableContentValues = {
  label: string;
  value?:
    | string
    | string[]
    | DateRangePlacementSummaryValues
    | TargetingPlacementSummaryValues
    | ImpressionsPlacementSummaryValues
    | WeblabPlacementSummaryValues
    | null;
};

export type GetPlacementTableRowData<PlacementTableRowData> = (params: GetPlacementTableRowValuesParams) => PlacementTableRowData;

export type SummarySectionConfig<OverviewTableData, TContent, PlacementTableRowData, TGlobalContent = undefined> = {
  ContentTableContent: FC<ContentTableContentProps<TGlobalContent>>;
  getContentTableData: GetContentTableData<TContent, TGlobalContent>;
  getOverviewTableData: GetOverviewTableData<OverviewTableData>;
  getPlacementDisplayName: GetPlacementDisplayName;
  getPlacementTableRowData: GetPlacementTableRowData<PlacementTableRowData>;
  supportsCustomizedSymphonyPreview?: boolean;
  GenericPlacementSummaryTableRow?: FC<{
    flavorName: string;
    placementTableData: Record<string, PlacementTableContentValues>;
  }>;
  campaignConfig?: CampaignConfigProviderCampaignFragment;
};
